import useCreateEstimate from '@/api/mutations/estimates/useCreateEstimate';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import useGetProjectEstimates from '@/api/queries/project/useGetProjectEstimates';
import useGetProjectSolarPotential from '@/api/queries/project/useGetProjectSolarPotential';
import { Button, ButtonDropdown, SkeletonLoader } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ArchivedEstimatesSection from './ArchivedEstimatesSection';
import EstimateCard from './EstimateCard';

const EstimatesPanel: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();
  const hasHeatDesignTool = useMyAccountHasFeature('hasHeatDesignTool');
  const hasSolarDesignTool = useMyAccountHasFeature('hasSolarDesignTool');
  const { project, isLoading } = useGetProjectEstimates(projectId);
  const { solarPotential } = useGetProjectSolarPotential(projectId);
  const { mutate: createEstimate, isPending: isCreatingEstimate } =
    useCreateEstimate({
      onSuccess: (data, variables) => {
        history.push(
          `/projects/${variables.projectId}/estimates/${data.createEstimate.id}`
        );
      },
    });

  const canDesignSolar = !!solarPotential?.available && !!solarPotential.centre;

  const estimates = project?.estimates ?? [];
  const activeEstimates = estimates.filter((e) => e.status !== 'ARCHIVED');
  const archivedEstimates = estimates.filter((e) => e.status === 'ARCHIVED');

  if (isLoading) {
    return (
      <div
        className="@container space-y-4"
        aria-busy="true"
        aria-label="Loading estimates"
      >
        <div className="flex justify-end">
          <SkeletonLoader className="h-8 w-32 rounded-md" />
        </div>
        {[0, 1].map((i) => (
          <SkeletonLoader key={i} className="h-40 w-full rounded-md" />
        ))}
      </div>
    );
  }

  const createOptions = [
    ...(hasHeatDesignTool
      ? [
          {
            label: 'Heat pump estimate',
            onClick: () =>
              createEstimate({
                projectId,
                categories: ['HEAT_PUMP'],
              }),
          },
        ]
      : []),
    ...(hasSolarDesignTool
      ? [
          {
            label: 'Solar estimate',
            // When the solar potential has been fetched and is usable,
            // create through the full-page designer; otherwise create
            // a blank SOLAR estimate to fill in manually in the
            // builder.
            onClick: () => {
              if (canDesignSolar) {
                history.push(`/projects/${projectId}/solar-designer`);
              } else {
                createEstimate({
                  projectId,
                  categories: ['SOLAR'],
                });
              }
            },
          },
        ]
      : []),
    {
      label: 'General estimate',
      onClick: () =>
        createEstimate({
          projectId,
          categories: [],
        }),
    },
  ];
  const buttonVariant = activeEstimates.length === 0 ? undefined : 'secondary';

  return (
    <div className="@container space-y-4">
      <div className="flex justify-end">
        {createOptions.length === 1 ? (
          <Button
            variant={buttonVariant}
            onClick={createOptions[0].onClick}
            isProcessing={isCreatingEstimate}
            disabled={isCreatingEstimate}
            size="sm"
          >
            Create estimate
          </Button>
        ) : (
          <ButtonDropdown
            variant={buttonVariant}
            isProcessing={isCreatingEstimate}
            disabled={isCreatingEstimate}
            size="sm"
            dropdownOptions={{ items: createOptions }}
          >
            Create estimate
            <UntitledIcon name="chevron-down" className="size-4" />
          </ButtonDropdown>
        )}
      </div>

      {activeEstimates.length > 0 && (
        <ul aria-label="Estimates" className="list-none p-0 space-y-4">
          {activeEstimates.map((estimate) => (
            <li key={estimate.id}>
              <EstimateCard projectId={projectId} estimate={estimate} />
            </li>
          ))}
        </ul>
      )}

      <ArchivedEstimatesSection
        projectId={projectId}
        estimates={archivedEstimates}
      />
    </div>
  );
};

export default EstimatesPanel;
